import type React from 'react';
import {Alert, Skeleton, Stack} from '@mui/material';
import {useOutletContext} from 'react-router';
import {useMarketActions, useMarketQuery} from '../../hooks/useMarket';
import {ProfileSection} from '../components/ProfileSection';
import {DriversSection} from '../market/DriversSection';
import {MarketChart} from '../market/MarketChart';
import {SourceRegistry} from '../market/SourceRegistry';
import type {CategoryProfileOutletContext} from '../profileTypes';

/**
 * Market index (spec 4.10, build plan section 5): what the category pays against its cost drivers. Price paid comes
 * from the internal benchmark; drivers from Eurostat, ECB, Databento (when licensed) and uploaded series.
 */
export const MarketIndexTab: React.FC = () => {
    const {databagId, path, header} = useOutletContext<CategoryProfileOutletContext>();
    const {data, isLoading, isError} = useMarketQuery(databagId, path);
    const actions = useMarketActions(databagId, path);

    if (isError) return <Alert severity="error">Could not load the market index.</Alert>;
    if (isLoading || !data) return <Skeleton variant="rounded" height={420}/>;

    return (
        <Stack gap={2}>
            {data.priceIndex.length === 0 ? (
                <Alert severity="info">
                    {`No price-paid index for ${header.name} yet: it is built from items with a high-confidence internal benchmark `}
                    (see Benchmarks). The drivers below still show how the market moved.
                </Alert>
            ) : data.priceIndex.length < 6 && (
                <Alert severity="info">Fewer than 6 months of price paid: correlations need at least 6.</Alert>
            )}
            <ProfileSection title="Price paid against cost drivers"
                            subtitle={data.priceIndexItems ? `Index of ${data.priceIndexItems} benchmarked items, start = 100` : 'Drivers, start = 100'}>
                <MarketChart data={data}/>
            </ProfileSection>
            <ProfileSection title="Cost drivers">
                <DriversSection data={data} actions={actions}/>
            </ProfileSection>
            <SourceRegistry databagId={databagId} actions={actions}/>
        </Stack>
    );
};
